import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { listOrders } from '../../services/orders'
import { listMyDisputes, createDispute } from '../../services/disputes'
import { getUser, logout } from '../../services/helpers'
import MobileBottomNav from '../../components/MobileBottomNav'
import {
  CalendarIcon,
  TicketIcon,
  ShoppingBagIcon,
  ExclamationTriangleIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  TagIcon,
  CheckCircleIcon,
  XCircleIcon,
  PlusIcon,
  BuildingOffice2Icon
} from '@heroicons/react/24/outline'

const AttendeeDisputesPage = () => {
  const navigate = useNavigate()
  const [disputes, setDisputes] = useState([])
  const [loading, setLoading] = useState(true)
  const [orders, setOrders] = useState([])
  const [selectedDispute, setSelectedDispute] = useState(null)
  const [showDisputeForm, setShowDisputeForm] = useState(false)
  const [disputeForm, setDisputeForm] = useState({ orderId: '', subject: '', message: '' })
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [user, setUser] = useState(null)
  const displayName = user?.name || user?.email || 'Attendee'

  useEffect(() => {
    const u = getUser()
    setUser(u)
  }, [])

  useEffect(() => {
    fetchDisputes()
    fetchOrders()
  }, [])

  const fetchDisputes = async () => {
    setLoading(true)
    const res = await listMyDisputes()
    if (res.status === 200) {
      setDisputes(res.data || [])
    }
    setLoading(false)
  }

  const fetchOrders = async () => {
    const res = await listOrders()
    if (res.status === 200) {
      setOrders(res.data || [])
    }
  }

  const handleDisputeFormChange = (e) => {
    const { name, value } = e.target
    setDisputeForm(prev => ({ ...prev, [name]: value }))
  }

  const handleCreateDispute = async (e) => {
    e.preventDefault()
    try {
      const res = await createDispute(disputeForm)
      if (res.status === 200) {
        setDisputes(prev => [res.data, ...prev])
        setShowDisputeForm(false)
        setDisputeForm({ orderId: '', subject: '', message: '' })
        setSuccess('Dispute submitted successfully')
        setTimeout(() => setSuccess(''), 3000)
      } else {
        setError(res.error || 'Failed to create dispute')
      }
    } catch (err) {
      setError('Failed to create dispute')
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'RESOLVED':
        return 'text-green-600 bg-green-50 border-green-200'
      case 'OPEN':
        return 'text-yellow-600 bg-yellow-50 border-yellow-200'
      case 'CLOSED':
        return 'text-gray-600 bg-gray-50 border-gray-200'
      default:
        return 'text-gray-600 bg-gray-50 border-gray-200'
    }
  }

  const tabs = [
    { id: 'events', label: 'Discover Events', icon: CalendarIcon, path: '/events' },
    { id: 'offers', label: 'Browse Offers', icon: TagIcon, path: '/offers' },
    { id: 'centers', label: 'Centers', icon: BuildingOffice2Icon, path: '/centers' },
    { id: 'tickets', label: 'My Tickets', icon: TicketIcon, path: '/account/tickets' },
    { id: 'orders', label: 'Order History', icon: ShoppingBagIcon, path: '/account/orders' },
    { id: 'disputes', label: 'Disputes', icon: ExclamationTriangleIcon, path: '/account/disputes' },
    { id: 'profile', label: 'Profile', icon: UserCircleIcon, path: '/account/profile' }
  ]

  return (
    <div className="min-h-screen bg-slate-50 flex font-sans">
      {/* Sidebar Navigation - Hidden on mobile, shown on desktop */}
      <aside className={`hidden lg:block fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-slate-200`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center gap-3 px-6 h-16 border-b border-slate-100">
            <Link to="/" className="flex items-center gap-2 group">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-600 to-emerald-500 flex items-center justify-center text-white shadow-sm transition-transform group-hover:scale-105">
                <TicketIcon className="w-5 h-5" />
              </div>
              <span className="text-xl font-bold text-slate-900 tracking-tight">Obule</span>
            </Link>
            <button onClick={() => setMobileMenuOpen(false)} className="ml-auto lg:hidden">
              <XMarkIcon className="w-6 h-6 text-slate-400" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
            {tabs.map((tab) => {
              const Icon = tab.icon
              const currentPath = window.location.pathname
              const isActive = tab.id === 'centers' 
                ? currentPath.startsWith('/centers')
                : currentPath === tab.path
              
              return (
                <Link
                  key={tab.id}
                  to={tab.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 ${isActive
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                    }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-emerald-600' : 'text-slate-400'}`} />
                  {tab.label}
                </Link>
              )
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-slate-100">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                <UserCircleIcon className="w-6 h-6" />
              </div>
              <div className="overflow-hidden">
                <p className="text-sm font-semibold text-slate-900 truncate">{displayName}</p>
                <p className="text-xs text-slate-500 truncate">{user?.email}</p>
              </div>
            </div>
            <button
              onClick={() => {
                logout()
                navigate('/auth/login')
              }}
              className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors border border-transparent hover:border-red-100"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4" />
              Sign Out
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 lg:ml-64 min-w-0 flex flex-col pb-20 lg:pb-0">
        {/* Top Mobile Header */}
        <header className="lg:hidden bg-white border-b border-slate-200 flex items-center justify-center px-4 h-16 sticky top-0 z-40">
          <span className="font-semibold text-slate-900">Disputes</span>
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-8 flex items-center justify-between">
              <div>
                <h1 className="text-4xl font-bold text-slate-900 mb-2">Disputes</h1>
                <p className="text-slate-600">Manage and resolve issues with your orders</p>
              </div>
              <button
                onClick={() => setShowDisputeForm(true)}
                className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all shadow-lg shadow-red-600/30"
              >
                <PlusIcon className="w-5 h-5" />
                Report Issue
              </button>
            </div>

            {/* Notifications */}
            {error && (
              <div className="mb-6 p-4 rounded-xl bg-red-50 border border-red-100 text-red-700 flex items-center gap-3">
                <XCircleIcon className="w-5 h-5 shrink-0" />
                <span className="text-sm font-medium">{error}</span>
                <button onClick={() => setError('')} className="ml-auto">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            )}
            {success && (
              <div className="mb-6 p-4 rounded-xl bg-green-50 border border-green-100 text-green-700 flex items-center gap-3">
                <CheckCircleIcon className="w-5 h-5 shrink-0" />
                <span className="text-sm font-medium">{success}</span>
                <button onClick={() => setSuccess('')} className="ml-auto">
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
            )}

            {/* Disputes List */}
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent"></div>
                  <p className="mt-4 text-slate-600">Loading disputes...</p>
                </div>
              </div>
            ) : disputes.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-24 h-24 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-6">
                  <CheckCircleIcon className="w-12 h-12 text-green-500" />
                </div>
                <h3 className="text-2xl font-semibold text-slate-900 mb-2">No disputes found</h3>
                <p className="text-slate-600 mb-6">You have no open disputes. Everything looks good!</p>
                <button
                  onClick={() => setShowDisputeForm(true)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 font-semibold"
                >
                  <PlusIcon className="w-5 h-5" />
                  Report an Issue
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {disputes.map(dispute => (
                  <div
                    key={dispute._id}
                    onClick={() => setSelectedDispute(selectedDispute?._id === dispute._id ? null : dispute)}
                    className="bg-white rounded-2xl border border-slate-200 p-6 cursor-pointer hover:border-emerald-300 hover:shadow-lg transition-all"
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold text-slate-900 mb-1">{dispute.subject}</h4>
                        <p className="text-sm text-slate-500">
                          Order ID: {typeof dispute.orderId === 'object' ? dispute.orderId?._id : dispute.orderId}
                        </p>
                      </div>
                      <span className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase border ${getStatusColor(dispute.status)}`}>
                        {dispute.status}
                      </span>
                    </div>

                    {selectedDispute?._id === dispute._id && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <div className="mb-4">
                          <p className="text-sm font-semibold text-slate-700 mb-2">Your Message:</p>
                          <p className="text-sm text-slate-600 bg-slate-50 p-3 rounded-lg">{dispute.message}</p>
                        </div>
                        {dispute.resolutionNotes && (
                          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                            <p className="text-sm font-bold text-green-900 mb-2">Resolution:</p>
                            <p className="text-sm text-green-800">{dispute.resolutionNotes}</p>
                          </div>
                        )}
                        <div className="mt-4 text-xs text-slate-400">
                          Created: {new Date(dispute.createdAt).toLocaleString()}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* New Dispute Modal */}
            {showDisputeForm && (
              <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl">
                  <h3 className="text-2xl font-bold text-slate-900 mb-6">Report an Issue</h3>
                  <form onSubmit={handleCreateDispute} className="space-y-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Select Order</label>
                      <select
                        name="orderId"
                        value={disputeForm.orderId}
                        onChange={handleDisputeFormChange}
                        className="w-full rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                        required
                      >
                        <option value="">Choose an order...</option>
                        {orders.map(o => (
                          <option key={o._id} value={o._id}>
                            {o.items && o.items[0]?.eventTitle ? o.items[0].eventTitle : o._id} - ₦{(o.total || 0).toLocaleString()}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Subject</label>
                      <input
                        name="subject"
                        value={disputeForm.subject}
                        onChange={handleDisputeFormChange}
                        className="w-full rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                        placeholder="Brief subject"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Description</label>
                      <textarea
                        name="message"
                        value={disputeForm.message}
                        onChange={handleDisputeFormChange}
                        rows={4}
                        className="w-full rounded-lg border-slate-300 focus:border-emerald-500 focus:ring-emerald-500"
                        placeholder="Describe your issue in detail..."
                        required
                      />
                    </div>
                    <div className="flex gap-3 pt-4">
                      <button
                        type="button"
                        onClick={() => setShowDisputeForm(false)}
                        className="flex-1 py-3 border-2 border-slate-300 rounded-lg text-slate-700 font-semibold hover:bg-slate-50 transition"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="flex-1 py-3 bg-red-600 text-white rounded-lg font-semibold hover:bg-red-700 transition shadow-lg"
                      >
                        Submit Dispute
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            )}
          </div>
        </main>
        <MobileBottomNav />
      </div>
    </div>
  )
}

export default AttendeeDisputesPage

